import React, { useCallback, useEffect, useState, Suspense } from 'react';
import { Grid, Box, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import DashboardTemplate from '../../components/dashboard/DashboardTemplate';
import WidgetCard from '../../components/home/WidgetCard';
import KpiStrip from '../../components/home/KpiStrip';
import DashboardCalendarContainer from './calendar/DashboardCalendarContainer';
import WidgetErrorBoundary from '../../components/home/WidgetErrorBoundary';
import WidgetSkeleton from '../../components/home/WidgetSkeleton';
import GenericWidgetContainer from '../home/GenericWidgetContainer';
import { getHomeLayout } from '../../lib/api/home';
import { COMMON_FALLBACK_WIDGETS, EXTERNAL_WIDGETS, SPECIAL_WIDGETS } from '../../widgets/registry';
import { WidgetSummaryCacheProvider } from '../../hooks/useWidgetSummary';

// 메인화면 역할별 구성 1단계(2026-10-23). 참고: docs "CRM 역할별 메인화면 구성" 조사.
// 서버가 로그인 사용자 기준으로 세트(공통/영업담당자/영업관리자/관리부·회계/경영진/기술지원)를
// 판정해서 위젯 목록을 내려준다(GET /api/v1/home/layout) - 프론트는 그 목록을 그대로 그린다.
// 캘린더는 기존 전체화면 토글 동작을 그대로 유지한다(레이아웃 응답에 있어도 특별 취급).
const DashboardContainer = () => {
  const [calFullTyp, setCalFullTyp] = useState(false);
  const [layout, setLayout] = useState({ loading: true, widgets: [], setId: null, error: null });
  const { user } = useSelector(({ auth }) => ({
    user: auth.user,
  }));

  const toggleCalFull = useCallback(() => {
    setCalFullTyp((v) => !v);
  }, []);

  useEffect(() => {
    let active = true;
    getHomeLayout()
      .then((res) => {
        if (!active) return;
        setLayout({ loading: false, widgets: res.data?.widgets || [], setId: res.data?.setId, error: null });
      })
      .catch((err) => {
        if (!active) return;
        // 재검토(Fable, 2026-10-23) 회귀 1: 레이아웃 API가 실패해도 화면이 통째로 비면 안 된다 -
        // 클라이언트 COMMON 폴백(캘린더/결재 대기함/내 상신/공지)으로 이전 화면과 동일하게 유지.
        setLayout({ loading: false, widgets: COMMON_FALLBACK_WIDGETS, setId: 'COMMON', error: err });
      });
    return () => {
      active = false;
    };
  }, []);

  if (calFullTyp) {
    return (
      <DashboardTemplate user={user}>
        <WidgetCard
          title="스케줄 관리"
          action={<Button size="small" onClick={toggleCalFull}>접기</Button>}
        >
          <DashboardCalendarContainer toggleCalFull={toggleCalFull} full />
        </WidgetCard>
      </DashboardTemplate>
    );
  }

  const renderableWidgets = layout.widgets.filter((w) => !SPECIAL_WIDGETS.includes(w.widgetCd));
  // 재검토(Fable, 2026-10-23) 회귀 2: 로딩 중에는 hasSchedule이 항상 false라 캘린더가 늦게
  // 나타나며 레이아웃이 점프했다 - COMMON_SCHEDULE은 모든 세트에 항상 병합되므로(HomeLayoutService)
  // 로딩 중에도 자리를 미리 예약해 둔다.
  const hasSchedule = layout.loading || layout.widgets.some((w) => w.widgetCd === 'COMMON_SCHEDULE');
  // 상단 KPI 띠(KpiStrip)는 EXTERNAL(캘린더/결재/공지)을 뺀 "일반 위젯"만 쓴다 - EXTERNAL은
  // getWidgetSummary 대상이 아니라서(각자 다른 API/redux slice를 쓴다) 숫자 하나로 압축할 수 없다.
  const kpiWidgets = renderableWidgets.filter((w) => !EXTERNAL_WIDGETS[w.widgetCd]);

  return (
    <DashboardTemplate user={user}>
      {layout.error && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          홈 화면 구성을 불러오지 못했습니다. 새로고침해 주세요.
        </Typography>
      )}

      {/* 비주얼 리뉴얼 2단계(2026-10-23) §KPI 중복호출: KpiStrip과 아래 위젯 그리드가 같은
          widgetCd를 공유 캐시로 한 번만 fetch하도록 두 쪽을 하나의 Provider로 감싼다
          (hooks/useWidgetSummary.js 참고) - 새 백엔드 호출은 없다. */}
      <WidgetSummaryCacheProvider>
      {!layout.loading && <KpiStrip widgets={kpiWidgets} />}

      {/* 폴리시(2단계, 2026-10-23) §2: 기본 Grid는 alignItems:stretch라 같은 행의 카드가
          제일 키 큰 카드(보통 일정 아젠다, 7일치) 높이까지 억지로 늘어나 짧은 카드(결재 대기함
          등)에 큰 빈 공간이 남았다. flex-start로 바꿔 각 카드가 내용 높이대로 자연스럽게
          앉도록 한다 - WidgetCard의 height:'100%'는 이제 "그 행에서 제일 큰 카드"가 아니라
          "자기 내용"에 맞춰진다. */}
      <Grid container spacing={1} alignItems="flex-start">
        {hasSchedule && (
          // EXTERNAL 위젯(결재/공지)은 내부에 그리드 툴바가 있어 1024px(md)에서 반폭이면
          // 겹친다 - lg(≥1200px)부터만 반으로 나누고, md 구간(900~1200)에서는 전체 폭을 쓴다.
          <Grid item xs={12} lg={6}>
            <WidgetCard
              title="일정"
              subtitle="이번 주"
              action={<Button size="small" onClick={toggleCalFull}>전체 보기 →</Button>}
            >
              <DashboardCalendarContainer toggleCalFull={toggleCalFull} />
            </WidgetCard>
          </Grid>
        )}

        {layout.loading &&
          [1, 2, 3].map((i) => (
            <Grid item xs={12} lg={6} key={`skeleton-${i}`}>
              <WidgetCard title=" ">
                <WidgetSkeleton />
              </WidgetCard>
            </Grid>
          ))}

        {!layout.loading &&
          renderableWidgets.map((w) => {
            const ExternalComponent = EXTERNAL_WIDGETS[w.widgetCd];
            const gridW = Math.min(12, w.w || 6);
            // 일반 위젯(GenericWidgetContainer)은 카드가 가벼워 md(1024px)에서도 반폭이 깨지지
            // 않지만, EXTERNAL 위젯은 내부 목록이 넓어 md에서는 전체 폭을 줘야 한다.
            const breakpointProps = ExternalComponent
              ? { xs: 12, lg: gridW }
              : { xs: 12, md: gridW };
            return (
              <Grid item {...breakpointProps} key={w.widgetCd}>
                <WidgetErrorBoundary widgetCd={w.widgetCd}>
                  <Suspense fallback={<WidgetCard title={w.widgetNm}><WidgetSkeleton /></WidgetCard>}>
                    {ExternalComponent ? (
                      <WidgetCard title={w.widgetNm}>
                        <ExternalComponent />
                      </WidgetCard>
                    ) : (
                      <GenericWidgetContainer widgetCd={w.widgetCd} title={w.widgetNm} renderTyp={w.renderTyp} />
                    )}
                  </Suspense>
                </WidgetErrorBoundary>
              </Grid>
            );
          })}
      </Grid>
      </WidgetSummaryCacheProvider>

      {!layout.loading && !layout.error && renderableWidgets.length === 0 && !hasSchedule && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            표시할 위젯이 없습니다. 관리자에게 문의해 주세요.
          </Typography>
        </Box>
      )}
    </DashboardTemplate>
  );
};

export default DashboardContainer;
